import React, { useState } from 'react';
import styled from 'styled-components';
import emailjs from "emailjs-com";

// Common styled components
const FormContainer = styled.div`
  width: 100%;
  max-width: ${props => props.maxWidth || '600px'};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 60px 0px;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color || '#184A2C'};
`;

const Input = styled.input`
  padding: 12px;
  border-radius: 4px;
  background-color: ${props => props.bgColor || 'rgba(50, 50, 50, 0.3)'};
  border: 1px solid ${props => props.borderColor || 'rgba(80, 80, 80, 0.5)'};
  color: ${props => props.textColor || 'white'};
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    padding: 10px;
  }

  @media (max-width: 768px) {
    padding: 8px;
  }
  
  &:focus {
    outline: none;
    border-color: #66BFBF;
  }
`;

const Select = styled.select`
  padding: 12px;
  border-radius: 4px;
  background-color: ${props => props.bgColor || 'rgba(50, 50, 50, 0.3)'};
  border: 1px solid ${props => props.borderColor || 'rgba(80, 80, 80, 0.5)'};
  color: ${props => props.textColor || '#184A2C'};
  font-size: 14px;
  width: 100%;
  appearance: none;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
  
  &:focus {
    outline: none;
    border-color: #66BFBF;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  
  &::after {
    content: '▼';
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 12px;
    color: ${props => props.arrowColor || '#184A2C'};
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border-radius: 4px;
  background-color: ${props => props.bgColor || 'rgba(50, 50, 50, 0.3)'};
  border: 1px solid ${props => props.borderColor || 'rgba(80, 80, 80, 0.5)'};
  color: ${props => props.textColor || '#184A2C'};
  font-size: 14px;
  width: 100%;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
  
  &:focus {
    outline: none;
    border-color: #66BFBF;
  }
`;

const Button = styled.button`
  background-color: ${props => props.bgColor || '#E84855'};
  color: ${props => props.textColor || 'white'};
  padding: 16px 24px;
  font-size: 16px;
  border-radius: 4px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease;
  width: 100%;

  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 14px;
  }
`;

const Contact = styled.span`
  font-family: raritas, sans-serif;
  font-style: normal;
  color: #184A2C;
  font-size: 18px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Message = styled.p`
  color: ${(props) => (props.success ? "green" : "red")};
  font-size: 14px;
  margin: 4px 0 0 0;
`;

const ContactSection = styled.div`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
  box-sizing: border-box;
`;

const ContactWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const InfoSection = styled.div`
  flex: 1;
  min-width: 300px;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: 600px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const Heading = styled.h2`
  font-size: 46px;
  color: #184A2C;
  margin: 0 0 16px 0;
  font-family: 'Clash Grotesk', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Paragraph = styled.p`
  font-size: ${props => props.small ? '14px' : '16px'};
  color: #184A2C;
  line-height: 1.6;
  margin: ${props => props.margin || '0 0 40px 0'};
`;

// Contact form component
const ContactForm = () => {
  const [formData, setFormData] = useState({ 
    fullName: "", 
    workEmail: "", 
    message: "",
    businessName: "",
    whatsappNumber: "" 
  });
  
  const [status, setStatus] = useState({ 
    loading: false, 
    success: null, 
    message: "" 
  });

  const [errors, setErrors] = useState({});
  
  // Handle Input Change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Validate Form
  const validateForm = () => {
    let errors = {};
  
    if (!formData.whatsappNumber) {
      errors.whatsappNumber = "WhatsApp number is required.";
    } else if (!/^\+\d{7,15}$/.test(formData.whatsappNumber)) {
      errors.whatsappNumber = "Enter a valid WhatsApp number (e.g., +17112373691).";
    }
  
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Form Submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setStatus({ loading: true, success: null, message: "" });
    
    emailjs
      .send(
        "service_e7thych",   // Replace with your Email.js Service ID
        "template_e04ztke",  // Replace with your Email.js Template ID
        {
          name: formData.fullName,
          email: formData.workEmail,
          message: formData.message,
          number: formData.whatsappNumber,
          business: formData.businessName,
        },
        "lMjHqDPRgZhm9hwZV"  // Replace with your Email.js Public Key
      )
      .then(
        () => setStatus({ loading: false, success: true, message: "Email sent successfully!" }),
        () => setStatus({ loading: false, success: false, message: "Failed to send email. Try again later." })
      )
      .finally(() => {
        setTimeout(() => {
          setFormData({ fullName: "", workEmail: "", message: "", businessName: "", whatsappNumber: "" });
          setStatus((prev) => ({ ...prev, loading: false, message: null }));
        }, 2000);
      });
  };
  
  return (
    <ContactSection>
      <ContactWrapper>
        <InfoSection>
          <SectionHeader>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20.5C16.6944 20.5 20.5 16.6944 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5Z" stroke="#184A2C" strokeWidth="1.5"/>
              <path d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10" stroke="#184A2C" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M12 10V7" stroke="#184A2C" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <Contact>Contact us</Contact>
          </SectionHeader>
          
          <Heading>Get in touch</Heading>
          <Paragraph>
            Please feel free to send us any questions, feedback or suggestions you might have.
          </Paragraph>
          
          <div style={{ marginTop: '60px' }}>
            <Paragraph small margin="0 0 30px 0">
              arrangemessage empowers businesses to implement and sustain cutting-edge AI solutions with seamless deployment and intuitive management, redefining the way industries leverage Voice-Driven Intelligence
            </Paragraph>
          </div>
        </InfoSection>
        
        <FormContainer>
          <Row>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="John Doe"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Work Email</Label>
              <Input
                type="email"
                name="workEmail"
                value={formData.workEmail}
                onChange={handleChange}
                placeholder="john@arrangemessage.com"
              />
            </FormGroup>
          </Row>
          
          <Row>
            <FormGroup>
              <Label>Whatsapp Number</Label>
              <Input
                type="tel"
                name="whatsappNumber"
                value={formData.whatsappNumber}
                onChange={handleChange}
                placeholder="+17112373691"
              />
              {errors.whatsappNumber && <Message>{errors.whatsappNumber}</Message>}
            </FormGroup>
            
            <FormGroup>
              <Label>Business Name</Label>
              <Input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Apple"
              />
            </FormGroup>
          </Row>
          
          <FormGroup>
            <Label>Message</Label>
            <TextArea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message..."
            />
          </FormGroup>
         
          <Button onClick={handleSubmit} disabled={status.loading}>
            {status.loading ? "Sending..." : "Experience The Future"}
          </Button>
          
          {status.message && <Message success={status.success}>{status.message}</Message>}
        </FormContainer>
      </ContactWrapper>
    </ContactSection>
  );
};

export default ContactForm;